import { type Hex, InteractiveContainer, type Stroke } from '@acies/core';
import theme from './theme';

/**
 * How the StateContainer looks, state by state.
 *
 * Keyed by the styled state rather than the state, so hovering has no look of its own: the cursor is what says the container can be interacted with. Written out per state, so each can be read against the Design System and the compiler asks for a look the day a state is added.
 */
const LOOKS: Readonly<Record<StateContainer['styledState'], { readonly fill: Hex; readonly stroke: Stroke | null }>> = {
	disabled: { fill: theme.colors.fill.weak, stroke: null },
	focused: { fill: theme.colors.fill.focus, stroke: theme.strokes.focus },
	focusedPressed: { fill: theme.colors.fill.white, stroke: theme.strokes.soft },
	idle: { fill: theme.colors.fill.white, stroke: theme.strokes.soft },
	pressed: { fill: theme.colors.fill.white, stroke: theme.strokes.soft },
};

/**
 * The StateContainer implements the disabled and focused styles that we have in the Design System.
 *
 * The sole purpose is to be extendable, so we can abstract the state styling away from sub components.
 *
 * Button and Dropdown are among the sub classes that will extend from this super class.
 */
export default class StateContainer extends InteractiveContainer {
	public constructor() {
		super();
		this.cursor = 'pointer';
		this.cornerRadius = 4;
		this.fill = theme.colors.fill.white;
		this.height = 32;
		this.minWidth = 32;
		this.stroke = theme.strokes.soft;
	}

	protected override enabledChanged(): void {
		super.enabledChanged();
		this.#updateCursorFromEnabledProperty();
	}

	protected override stateChanged(): void {
		super.stateChanged();
		const look = LOOKS[this.styledState];
		this.fill = look.fill;
		this.stroke = look.stroke;
	}

	#updateCursorFromEnabledProperty(): void {
		if (this.enabled) {
			this.cursor = 'pointer';
		} else {
			this.cursor = 'default';
		}
	}
}
customElements.define('ac-state-container', StateContainer);
